import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatAge } from "../../i18n/format";
import { Button } from "../../ui/Button";
import { ErrorMessage } from "../../ui/ErrorMessage";
import { Loading } from "../../ui/Spinner";
import { asDocument, type DashboardDocument } from "../dashboards/copy";
import { DashboardEditor } from "../dashboards/DashboardEditor";
import { dashboardWidgets, useNow } from "../dashboards/widgets";
import { refusedWidgets, saveErrors, widgetLabel } from "../dashboards/errors";
import { useEditedDashboardInput } from "../dashboards/input";
import { ActionDialog } from "../devices/actions/ActionDialog";
import { useDeviceActions } from "../devices/actions/useDeviceActions";
import { AddDeviceDialog } from "../devices/AddDeviceDialog";
import { LayerDialog } from "../devices/page/PageGrid";
import { WidgetGrid } from "../devices/page/WidgetGrid";
import { devicesQuery } from "../devices/queries";
import { ConfirmDialog } from "../ops/shared/ConfirmDialog";
import { ClaimObjectionsNotice } from "../organisation/ClaimObjections";
import { useOrg } from "../org/OrgProvider";
import {
  addToHome,
  homeKey,
  homeQuery,
  resetHome,
  saveHome,
  updateHomeTiles,
  type Home,
  type HomeLayer,
} from "./queries";
import { LayoutEdit } from "../../ui/LayoutEdit";

/** At most this many devices per add or dismiss (HomeDevicesInput.device_ids). */
const BATCH = 40;

/**
 * The home page (specs/home-page HOME-01 to HOME-08): widgets from many devices, the page the app opens on. Edit and
 * Save for me or the organisation, Reset, the devices not on it, new home tiles of their types (DTP-15), and Add your
 * first device in an empty organisation.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const { orgId } = useOrg();
  const org = orgId ?? "";
  const now = useNow();
  const qc = useQueryClient();
  const language = i18n.language;
  const home = useQuery({ ...homeQuery(org, language), enabled: !!orgId });
  const doc = useMemo(() => asDocument(home.data?.document), [home.data]);
  const widgets = useMemo(() => (doc ? dashboardWidgets(doc) : []), [doc]);
  const data = useEditedDashboardInput(org, widgets, language);
  const actions = useDeviceActions(
    org,
    data.docFor,
    data.infoFor,
    widgets.some((w) => w.type === "scene_button"),
  );
  const [editing, setEditing] = useState(false);
  const [choosing, setChoosing] = useState<DashboardDocument>();
  const [resetting, setResetting] = useState<HomeLayer>();
  const [picking, setPicking] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const empty = !!home.data && widgets.length === 0 && home.data.not_on_home.length === 0;
  const anyDevice = useInfiniteQuery({ ...devicesQuery(org, {}), enabled: empty });
  const noDevices = empty && anyDevice.data?.pages[0]?.items.length === 0;

  const done = (next: Home) => {
    qc.setQueryData(homeQuery(org, language).queryKey, next);
    setEditing(false);
    setChoosing(undefined);
    setResetting(undefined);
    setPicking(false);
  };
  const save = useMutation({
    mutationFn: ({ layer, document }: { layer: HomeLayer; document: DashboardDocument }) =>
      saveHome(org, language, layer, document),
    onSuccess: done,
  });
  const reset = useMutation({ mutationFn: (layer: HomeLayer) => resetHome(org, language, layer), onSuccess: done });
  const add = useMutation({
    mutationFn: async ({ ids, dismiss }: { ids: string[]; dismiss: boolean }) => {
      let last: Home | undefined;
      for (let i = 0; i < ids.length; i += BATCH)
        last = await addToHome(org, language, ids.slice(i, i + BATCH), dismiss);
      return last as Home;
    },
    onSuccess: done,
  });
  const tiles = useMutation({
    mutationFn: ({ typeId, dismiss }: { typeId: string; dismiss: boolean }) =>
      updateHomeTiles(org, language, typeId, dismiss),
    onSuccess: done,
  });

  // The editor stays open while a widget it added reads its device (#6).
  if (home.isPending || (doc && data.loading && !editing)) return <Loading />;
  if (home.isError) return <ErrorMessage error={home.error} onRetry={() => void home.refetch()} />;
  const h = home.data;
  const widgetName = (i: number) => widgetLabel(save.variables?.document ?? doc, data.input.points, i);
  const base: DashboardDocument = doc ?? { schema: 1, kind: "home", name: t("home.title"), widgets: [] };

  if (editing)
    return (
      <article className="p-6">
        <h1 className="text-xl font-semibold text-gray-900">{t("home.title")}</h1>
        <DashboardEditor
          doc={base}
          orgId={org}
          input={data.input}
          inputFor={data.inputFor}
          now={now}
          saving={save.isPending}
          errors={save.error ? saveErrors(save.error, t, widgetName) : undefined}
          refused={refusedWidgets(save.error)}
          missing={data.missing}
          onDraft={data.onDraft}
          onSave={(next) => {
            const document = { ...next, kind: "home" as const };
            if (h.can_edit_organisation) setChoosing(document);
            else save.mutate({ layer: "personal", document });
          }}
          onCancel={() => setEditing(false)}
        />
        <LayerDialog
          open={!!choosing}
          busy={save.isPending}
          text={t("home.saveForText")}
          onPick={(layer) => choosing && save.mutate({ layer, document: choosing })}
          onClose={() => setChoosing(undefined)}
        />
      </article>
    );

  const missing = h.not_on_home;
  return (
    <article className="p-6">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">{t("home.title")}</h1>
        {h.layer !== "none" && (
          <p className="text-sm text-gray-800">
            {t(`home.layer.${h.layer}`)}
            {h.updated_at ? ` · ${formatAge(h.updated_at, i18n.language, now)}` : ""}
          </p>
        )}
        {!noDevices && (
          <div className="ml-auto flex flex-wrap gap-2">
            <LayoutEdit>
              <Button
                variant="secondary"
                onClick={() => {
                  save.reset();
                  setEditing(true);
                }}
              >
                {t("home.edit")}
              </Button>
            </LayoutEdit>
            {h.layer === "personal" && (
              <Button variant="secondary" onClick={() => setResetting("personal")}>
                {t("home.resetMine")}
              </Button>
            )}
            {h.layer === "organisation" && h.can_edit_organisation && (
              <Button variant="secondary" onClick={() => setResetting("organisation")}>
                {t("home.resetOrganisation")}
              </Button>
            )}
          </div>
        )}
      </header>
      <ClaimObjectionsNotice />

      {missing.length > 0 && (
        <p className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-900">
          {t("home.notOnHome", { count: missing.length })}
          <Button variant="secondary" onClick={() => setPicking(true)}>
            {t("home.add")}
          </Button>
          <Button
            variant="secondary"
            disabled={add.isPending}
            onClick={() => add.mutate({ ids: missing.map((d) => d.device_id), dismiss: true })}
          >
            {t("home.notNow")}
          </Button>
        </p>
      )}
      {(h.tile_updates ?? []).map((u) => (
        <p
          key={u.type_id}
          className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-900"
        >
          {t("home.newTile", { name: u.name, count: u.tiles })}
          <Button
            variant="secondary"
            disabled={tiles.isPending}
            onClick={() => tiles.mutate({ typeId: u.type_id, dismiss: false })}
          >
            {t("home.updateTiles")}
          </Button>
          <Button
            variant="secondary"
            disabled={tiles.isPending}
            onClick={() => tiles.mutate({ typeId: u.type_id, dismiss: true })}
          >
            {t("home.notNow")}
          </Button>
        </p>
      ))}
      {(add.isError || reset.isError || tiles.isError) && (
        <ErrorMessage error={add.error ?? reset.error ?? tiles.error} />
      )}

      {noDevices ? (
        <section className="mt-6 rounded-md border border-dashed border-gray-300 bg-white p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900">{t("home.firstDevice")}</h2>
          <p className="mt-2 text-sm text-gray-800">{t("home.firstDeviceText")}</p>
          <Button className="mt-4" onClick={() => setClaiming(true)}>
            {t("addDevice.open")}
          </Button>
        </section>
      ) : widgets.length === 0 ? (
        <p className="mt-6 rounded-md border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-800">
          {t("home.empty")}
        </p>
      ) : (
        <>
          {data.error && <ErrorMessage error={data.error} onRetry={data.refetch} />}
          <WidgetGrid
            widgets={widgets}
            input={data.input}
            inputFor={data.inputFor}
            actions={actions}
            now={now}
            orgId={org}
            label={t("home.widgets")}
          />
          <ActionDialog actions={actions} nameOf={(id) => data.input.devices?.[id]?.name ?? id} />
        </>
      )}

      <PickDialog
        open={picking}
        devices={missing}
        busy={add.isPending}
        onAdd={(ids) => add.mutate({ ids, dismiss: false })}
        onClose={() => setPicking(false)}
      />
      <AddDeviceDialog
        open={claiming}
        onClose={() => setClaiming(false)}
        onAdded={() => void qc.invalidateQueries({ queryKey: homeKey(org) })}
      />
      <ConfirmDialog
        open={!!resetting}
        title={t(resetting === "organisation" ? "home.resetOrganisationTitle" : "home.resetMineTitle")}
        confirmLabel={t("devicePage.reset")}
        onConfirm={() => resetting && reset.mutate(resetting)}
        onClose={() => setResetting(undefined)}
      >
        <p>{t(resetting === "organisation" ? "home.resetOrganisationText" : "home.resetMineText")}</p>
      </ConfirmDialog>
    </article>
  );
}

/** The devices not on the home page, each ticked, added together (HOME-06). */
function PickDialog({
  open,
  devices,
  busy,
  onAdd,
  onClose,
}: {
  open: boolean;
  devices: Home["not_on_home"];
  busy: boolean;
  onAdd: (ids: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) {
      setChosen(new Set(devices.map((x) => x.device_id)));
      d.showModal();
    } else if (!open && d.open) d.close();
    // The list is read when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: string) =>
    setChosen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={title}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="p-6">
        <h2 id={title} className="text-lg font-semibold text-gray-900">
          {t("home.pickTitle")}
        </h2>
        <ul className="mt-4 max-h-80 space-y-1 overflow-y-auto">
          {devices.map((d) => (
            <li key={d.device_id}>
              <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-900 hover:bg-gray-50">
                <input type="checkbox" checked={chosen.has(d.device_id)} onChange={() => toggle(d.device_id)} />
                {d.name}
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={busy || chosen.size === 0} onClick={() => onAdd([...chosen])}>
            {t("home.addChosen", { count: chosen.size })}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
